// Sugalarm — Household dashboard (cycle 14).
//
// The Household tab is now the live multi-member dashboard. Member
// management (add / archive / per-member CGM linking) moved to
// Settings → Members; this screen is read-only / switch-active only.
//
// Top: header strip with attention count.
// Middle: hero card for the active member.
// Bottom: card grid for every other non-archived member. Tapping a
// card calls `setActive(member_id)` and refreshes the App-level
// active-member hook so the Now / History / Alerts screens follow.

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import * as membersApi from "../../api/members";
import { GlancePill } from "../../components/kiosk/GlancePill";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { GlucoseSocketState } from "../../hooks/useGlucoseSocket";
import { useIsLandscape } from "../../hooks/useIsLandscape";
import { useThresholds } from "../../hooks/useThresholds";
import { lastSampleOutOfRange, selectCurrentGlucoseFor } from "../../lib/currentGlucose";
import { zoneOf } from "../../lib/glucose";
import { memberLockedFrom } from "../../lib/serviceLock";
import type { Member, User, Zone } from "../../lib/types";
import { HeroMemberCard } from "./HeroMemberCard";
import { MemberCard } from "./MemberCard";
import styles from "./HouseholdScreen.module.css";

interface Props {
  user: User | null;
  socket: GlucoseSocketState;
  units: "mg/dL" | "mmol/L";
  variant: FrameVariant;
  now: number;
  activeMemberId: string | null;
  refreshActive: () => Promise<void>;
}

function memberSort(a: Member, b: Member): number {
  if (a.is_self !== b.is_self) return a.is_self ? -1 : 1;
  return a.display_name.localeCompare(b.display_name);
}

export function HouseholdScreen({
  user: _user,
  socket,
  units,
  variant,
  activeMemberId,
  refreshActive,
}: Props) {
  const nav = useNavigate();
  const thresholds = useThresholds();
  const isLandscape = useIsLandscape();
  const mobileLandscape = variant === "mobile" && isLandscape;
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [switching, setSwitching] = useState<boolean>(false);

  // Member list — light static seed. The dashboard's live values
  // come from the socket; the member list itself rarely changes
  // outside the Members settings pane.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await membersApi.listMine();
      if (cancelled) return;
      if (r.ok) setMembers(r.data.members);
      else setError(r.error.message);
    })();
    return () => {
      cancelled = true;
    };
  }, [activeMemberId]);

  const sorted = useMemo(() => members.slice().sort(memberSort), [members]);
  const active = useMemo(
    () => sorted.find((m) => m.id === activeMemberId) ?? null,
    [sorted, activeMemberId],
  );
  const others = useMemo(
    () => sorted.filter((m) => m.id !== activeMemberId),
    [sorted, activeMemberId],
  );

  // Per-member readings + alert levels driven by the live socket.
  // Fall back to the dashboard seed if a frame for that member
  // hasn't arrived yet.
  const cardsById = useMemo(() => {
    const m = new Map(socket.dashboardCards.map((c) => [c.member_id, c]));
    return m;
  }, [socket.dashboardCards]);

  const onPickActive = async (memberId: string) => {
    if (memberId === activeMemberId || switching) return;
    setSwitching(true);
    const r = await membersApi.setActive(memberId);
    setSwitching(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    await refreshActive();
  };

  // ⚠ CYCLE 435 — CONFIRMATION IS PER MEMBER, NOT PER LINK. Cycle 433
  // proved the household's link by any member's frame; in a multi-member
  // household that let one member's frame confirm another member's number.
  // Every card now asks the ONE selector for ITS member, keyed on the
  // member the reading belongs to, and the selector samples `Date.now()`
  // for itself rather than the ticking `now` prop. DISPLAY ONLY.
  const activeView = selectCurrentGlucoseFor(socket, activeMemberId);
  const activeCurrent = activeView.displayValue;

  // Cycle 115: live glance values for the landscape top-right pill —
  // reuses the existing hero/staleness plumbing (no new fetch).
  // Cycle 433: the landscape glance pill shows the live number too, so it
  // follows the same display rule.
  const glanceZone: Zone = activeCurrent
    ? zoneOf(activeCurrent.sgv, thresholds)
    : "unknown";
  const glanceAge = activeView.ageMinutes ?? 0;

  // "M need attention" = members with a non-null current_alert_level
  // OR a per-member out-of-range reading. Counts the active member too.
  const attentionCount = sorted.filter((m) => {
    const card = cardsById.get(m.id);
    if (card?.current_alert_level) return true;
    // Cycle 435: the count keeps its pre-435 meaning — the member's LAST
    // SAMPLE out of range, fresh or not — answered in the lib so no raw
    // value is read here (regression-guard: it had silently narrowed).
    return lastSampleOutOfRange(selectCurrentGlucoseFor(socket, m.id));
  }).length;

  return (
    <div className={styles.root}>
      <div className={styles.headerRow}>
        <div>
          <h2 className={styles.title}>Household</h2>
          <div className={styles.subtitle}>
            {sorted.length} {sorted.length === 1 ? "member" : "members"}
            {attentionCount > 0 ? (
              <>
                {" · "}
                <span className={styles.subtitleAttention}>
                  {attentionCount} need
                  {attentionCount === 1 ? "s" : ""} attention
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className={styles.headerRight}>
          {mobileLandscape ? (
            <GlancePill
              current={activeCurrent}
              units={units}
              ageMin={glanceAge}
              zone={glanceZone}
              provider={active?.cgm_provider ?? null}
            />
          ) : null}
          {sorted.length === 1 ? (
            <button
              type="button"
              className={styles.manageLink}
              onClick={() => nav("/settings?section=members")}
            >
              {mobileLandscape ? "Manage →" : "Manage members in Settings →"}
            </button>
          ) : null}
        </div>
      </div>

      {error && <div className={styles.bannerError}>{error}</div>}

      {active ? (
        <HeroMemberCard
          displayName={active.display_name}
          avatarId={active.avatar_id}
          relationshipLabel={active.relationship_label}
          isSelf={active.is_self}
          cgmProvider={active.cgm_provider}
          view={activeView}
          currentAlertLevel={cardsById.get(active.id)?.current_alert_level ?? null}
          units={units}
          thresholds={thresholds}
          glass={variant === "mobile"}
          // Cycle 468: the owner's lock — the WS frame wins, the seed row
          // is the fallback (safety review M2).
          serviceLocked={memberLockedFrom(
            socket.serviceLockedByMember[active.id],
            cardsById.get(active.id)?.service_locked,
          )}
        />
      ) : (
        <div className={styles.muted}>
          {sorted.length === 0
            ? "Loading members…"
            : "Resolving active member…"}
        </div>
      )}

      {others.length > 0 ? (
        <>
          <div className={styles.sectionLabel}>Also in this household</div>
          <div
            className={`${styles.cardGrid} ${
              variant === "kiosk" ? styles.cardGridKiosk : ""
            }`}
          >
            {others.map((m) => {
              const card = cardsById.get(m.id);
              // Cycle 435: each card's number comes from the selector for
              // ITS member — confirmation is per member, never per link.
              const view = selectCurrentGlucoseFor(socket, m.id);
              return (
                <MemberCard
                  key={m.id}
                  memberId={m.id}
                  displayName={m.display_name}
                  avatarId={m.avatar_id}
                  relationshipLabel={m.relationship_label}
                  isSelf={m.is_self}
                  cgmProvider={m.cgm_provider}
                  view={view}
                  currentAlertLevel={card?.current_alert_level ?? null}
                  units={units}
                  thresholds={thresholds}
                  onPickActive={onPickActive}
                  glass={variant === "mobile"}
                  serviceLocked={memberLockedFrom(socket.serviceLockedByMember[m.id], card?.service_locked)}
                />
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
